import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useStore, formatPrice } from "../context/StoreContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const CheckoutPage = () => {
  const { cart, currency, clearCart } = useStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState(null);
  const [validating, setValidating] = useState(true);
  const [validationError, setValidationError] = useState(null);

  const [form, setForm] = useState({
    email: "",
    optIn: true,
    name: "",
    address: "",
    apartment: "",
    city: "",
    state: "",
    pin: "",
    phone: "",
    country: "INDIA",
    shipping: "standard",
    payment: "card",
    promo: "",
  });

  const u = (k) => (e) =>
    setForm({ ...form, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value });

  // Server-side validation of cart price + stock + totals (GST etc).
  useEffect(() => {
    if (cart.length === 0) {
      setValidating(false);
      return;
    }
    setValidating(true);
    setValidationError(null);
    const items = cart.map((l) => ({ product_id: l.id, size: l.size, quantity: l.quantity }));
    axios
      .post(`${API}/cart/validate`, { items })
      .then(({ data }) => {
        setTotals(data);
        setValidating(false);
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || "Could not validate cart";
        setValidationError(msg);
        setValidating(false);
      });
  }, [cart]);

  if (cart.length === 0) {
    return (
      <div className="pt-[140px] pb-20 text-center min-h-[60vh] px-4">
        <h1 className="font-display uppercase text-5xl">CART IS EMPTY</h1>
        <Link to="/shop" className="inline-block mt-6 btn-solid">Go to shop</Link>
      </div>
    );
  }

  const formValid = () =>
    form.email && form.name && form.address && form.city && form.state && form.pin && form.phone;

  const pay = async (e) => {
    e.preventDefault();
    if (!formValid()) {
      toast("FILL ALL REQUIRED FIELDS");
      return;
    }
    if (!totals) {
      toast("CART NOT VALIDATED YET");
      return;
    }
    setLoading(true);
    try {
      const items = cart.map((l) => ({ product_id: l.id, size: l.size, quantity: l.quantity }));
      const { data } = await axios.post(`${API}/checkout/razorpay/order`, {
        items,
        customer_email: form.email,
        customer_name: form.name,
        shipping_address: {
          address: form.address,
          apartment: form.apartment,
          city: form.city,
          state: form.state,
          pin: form.pin,
          phone: form.phone,
          country: form.country,
        },
      });

      const scriptOk = await loadRazorpayScript();
      if (!scriptOk) throw new Error("Could not load Razorpay");

      const options = {
        key: data.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: data.amount_paise,
        currency: data.currency,
        name: "Damage Culture",
        description: "Damaging the culture, one piece at a time.",
        order_id: data.razorpay_order_id,
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone,
        },
        notes: { source: "damageculture.in" },
        theme: { color: "#C42021" },
        handler: async (resp) => {
          try {
            const verify = await axios.post(`${API}/checkout/razorpay/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            clearCart();
            navigate(
              `/order-confirmation/${verify.data.order_id}?t=${encodeURIComponent(verify.data.order_token || "")}`
            );
          } catch (err) {
            toast("PAYMENT VERIFICATION FAILED. CONTACT US IF CHARGED.");
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => setLoading(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        toast("PAYMENT FAILED. TRY AGAIN.");
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Payment init failed";
      toast(msg.toUpperCase());
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-checkout" className="pt-[100px] pb-16">
      <div className="max-w-[1440px] mx-auto px-4 md:px-12">
        <h1 className="font-display uppercase text-5xl sm:text-7xl md:text-8xl leading-none mb-10">
          Checkout
        </h1>

        {validationError && (
          <div className="mb-6 border border-dc-red bg-dc-red/10 p-4 font-mono text-xs uppercase tracking-widest text-dc-red">
            {validationError}
          </div>
        )}

        <form onSubmit={pay} className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12 lg:gap-16">
          <div className="space-y-10">
            <section>
              <h2 className="font-display uppercase text-2xl md:text-3xl mb-4">Contact</h2>
              <input type="email" placeholder="EMAIL" value={form.email} onChange={u("email")} className="dc-input" data-testid="checkout-email" required />
              <label className="mt-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest">
                <input type="checkbox" checked={form.optIn} onChange={u("optIn")} className="w-4 h-4 accent-ink" data-testid="checkout-optin" />
                Email me with news and offers
              </label>
            </section>

            <section>
              <h2 className="font-display uppercase text-2xl md:text-3xl mb-4">Delivery</h2>
              <div className="space-y-3">
                <select value={form.country} onChange={u("country")} className="dc-input" data-testid="checkout-country">
                  <option>INDIA</option>
                  <option>UNITED STATES</option>
                  <option>UNITED KINGDOM</option>
                  <option>AUSTRALIA</option>
                  <option>CANADA</option>
                </select>
                <input className="dc-input" placeholder="FULL NAME" value={form.name} onChange={u("name")} data-testid="checkout-name" required />
                <input className="dc-input" placeholder="ADDRESS" value={form.address} onChange={u("address")} data-testid="checkout-address" required />
                <input className="dc-input" placeholder="APARTMENT, SUITE (OPTIONAL)" value={form.apartment} onChange={u("apartment")} data-testid="checkout-apartment" />
                <div className="grid grid-cols-2 gap-3">
                  <input className="dc-input" placeholder="CITY" value={form.city} onChange={u("city")} data-testid="checkout-city" required />
                  <input className="dc-input" placeholder="STATE" value={form.state} onChange={u("state")} data-testid="checkout-state" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input className="dc-input" placeholder="PIN / ZIP" value={form.pin} onChange={u("pin")} data-testid="checkout-pin" required />
                  <input className="dc-input" placeholder="PHONE" value={form.phone} onChange={u("phone")} data-testid="checkout-phone" required />
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-display uppercase text-2xl md:text-3xl mb-4">Payment</h2>
              <div className="rounded-[20px] shadow-card divide-y divide-ink/10">
                <label className="flex items-center p-4 cursor-pointer">
                  <input type="radio" name="payment" value="card" checked={form.payment === "card"} onChange={u("payment")} className="accent-ink mr-3" />
                  <span className="font-mono text-xs uppercase tracking-widest">Card / UPI / Wallets / Netbanking (Razorpay)</span>
                </label>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-ink/60 mt-3">
                YOU WILL BE PROMPTED TO PAY IN A SECURE RAZORPAY WINDOW. TEST CARD: 4111 1111 1111 1111.
              </p>
            </section>

            <button
              type="submit"
              disabled={loading || validating || validationError}
              data-testid="checkout-pay-now"
              className="btn-solid w-full text-base md:text-lg disabled:opacity-50"
            >
              {loading ? "OPENING RAZORPAY..." : validating ? "VALIDATING CART..." : totals ? `Pay ${formatPrice(totals.total, "INR")}` : "Pay Now"}
            </button>
          </div>

          <aside className="lg:sticky lg:top-[100px] lg:self-start card-surface rounded-[20px] shadow-card p-5 md:p-6 h-fit">
            <h3 className="font-display uppercase text-xl mb-4">Order summary</h3>
            <div className="space-y-3 mb-5">
              {cart.map((l) => (
                <div key={`${l.id}-${l.size}`} className="flex items-start gap-3">
                  <img src={l.image} alt={l.name} className="w-14 h-16 object-cover rounded-md" />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-base leading-tight truncate">{l.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-ink/60 mt-0.5">
                      SIZE {l.size} / x{l.quantity}
                    </p>
                  </div>
                  <p className="font-mono text-xs">
                    {formatPrice(l.priceInr * l.quantity, currency)}
                  </p>
                </div>
              ))}
            </div>
            <div className="border-t border-ink/15 pt-4 space-y-2 font-mono text-sm">
              <div className="flex justify-between">
                <span className="uppercase text-xs tracking-widest text-ink/70">Subtotal</span>
                <span data-testid="checkout-subtotal">{totals ? formatPrice(totals.subtotal, currency) : "..."}</span>
              </div>
              <div className="flex justify-between">
                <span className="uppercase text-xs tracking-widest text-ink/70">GST {totals?.gst_rate_pct ? `(${totals.gst_rate_pct}%)` : ""}</span>
                <span data-testid="checkout-gst">{totals ? formatPrice(totals.tax, currency) : "..."}</span>
              </div>
              <div className="flex justify-between">
                <span className="uppercase text-xs tracking-widest text-ink/70">Shipping</span>
                <span data-testid="checkout-shipping">
                  {totals ? (totals.shipping === 0 ? "FREE" : formatPrice(totals.shipping, currency)) : "..."}
                </span>
              </div>
              <div className="flex justify-between font-display uppercase text-xl mt-2 pt-2 border-t border-ink/15">
                <span>Total</span>
                <span data-testid="checkout-total">{totals ? formatPrice(totals.total, currency) : "..."}</span>
              </div>
              {currency === "USD" && totals && (
                <p className="font-mono text-[10px] uppercase tracking-widest text-ink/60 mt-3">
                  CHARGED IN INR. EQUIVALENT TO {formatPrice(totals.total, "USD")}.
                </p>
              )}
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
